import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, Input, viewChildren} from '@angular/core';
import {
    comparator,
    filterNullishRx,
    formatDate,
    fromControl,
    keyComparator,
    numericLocaleComparator,
    proBraLocComparator,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    map,
    merge,
    Observable,
    ReplaySubject,
    startWith,
    Subject,
    switchMap,
    take,
    tap,
} from 'rxjs';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {
    CheckboxGroupComponent,
    DatePipe,
    ControlMessagesComponent,
    PaginationComponent,
    applySortingToHeaders,
    SortableTableHeaderComponent,
} from '@brunata-angular/ui';
import {NgbDropdown, NgbDropdownMenu, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {InputDropdownCheckboxesComponent} from '@brunata-angular/ui';
import {AgentMeterIdsFormGroup, MeterForAgent} from '../select-meters-for-agent.model';
import {
    AgentMeterTableFormGroup,
    FilterMetersFormGroup,
    headers,
    MetersForAgentTableData,
    SelectableAgentMeter,
} from './select-meters-for-agent-table.model';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {
    MetersForAgentSorting,
    SelectMetersForAgentService,
} from '../select-meters-for-agent.service';
import {DateTime} from 'luxon';
import {uniq} from 'lodash-es';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TooltipComponent} from '@brunata-angular/ui';

type SelectMetersTableView = MetersForAgentTableData & AgentMeterTableFormGroup;

@Component({
    selector: 'app-select-meters-table',
    standalone: true,
    imports: [
        TranslatePipe,
        CommonModule,
        ReactiveFormsModule,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        CheckboxGroupComponent,
        ControlMessagesComponent,
        SortableTableHeaderComponent,
        PaginationComponent,
        NgbDropdown,
        NgbDropdownMenu,
        NgbDropdownToggle,
        DatePipe,
        InputDateGroupComponent,
        TooltipComponent,
    ],
    templateUrl: './select-meters-for-agent-table.component.html',
    styleUrl: './select-meters-for-agent-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectMetersForAgentTableComponent {
    private readonly headers = viewChildren(SortableTableHeaderComponent);

    @Input({required: true}) set formGroup(formGroup: AgentMeterIdsFormGroup) {
        this.formGroupSubject$.next(formGroup);
    }

    @Input({required: true}) set meters(meters: SelectableAgentMeter[]) {
        this.metersSubject$.next(meters);
    }

    private readonly formGroupSubject$ = new ReplaySubject<AgentMeterIdsFormGroup | undefined>(1);
    private readonly metersSubject$ = new BehaviorSubject<SelectableAgentMeter[]>([]);
    private readonly sortedMetersSubject$ = new Subject<SelectableAgentMeter[]>();

    private readonly formGroup$ = this.formGroupSubject$.pipe(filterNullishRx());

    private readonly metersForAgentService = inject(SelectMetersForAgentService);
    private readonly translateService = inject(TranslateService);

    private readonly formGroups$: Observable<AgentMeterTableFormGroup> = this.formGroup$.pipe(
        map(meterIdGroupsFormGroup => ({
            meterIdGroupsFormGroup,
            filterFormGroup: this.metersForAgentService.tableFormGroup.controls.filterFormGroup,
            visibleColumnsFormGroup:
                this.metersForAgentService.tableFormGroup.controls.selectedColumnsFormGroup,
        })),
    );

    private readonly sortedMeters$ = this.metersSubject$.pipe(
        take(1),
        map(meters => this.sortMeters(meters, {column: 'proBraLoc', direction: 'asc'})),
        switchMap(meters => this.sortedMetersSubject$.pipe(startWith(meters))),
    );

    private readonly selectableMeters$ = this.formGroup$.pipe(
        switchMap(formGroup =>
            combineLatest([
                formGroup.valueChanges.pipe(
                    startWith(formGroup),
                    map(() => formGroup.getRawValue()),
                ),
                this.sortedMeters$,
            ]).pipe(
                map(([{idsInGroup1, idsInGroup2}, meters]) => {
                    const mismatchedMeterIds = meters
                        .filter(meter => meter.meterCriteriaMismatch)
                        .map(meter => meter.meterIdAsNumber);
                    const validIdsInGroup1 = idsInGroup1.filter(
                        id => !mismatchedMeterIds.includes(id),
                    );
                    const validIdsInGroup2 = idsInGroup2.filter(
                        id => !mismatchedMeterIds.includes(id),
                    );

                    return meters.map(meter => ({
                        ...meter,
                        selectedGroup1: validIdsInGroup1.includes(meter.meterIdAsNumber),
                        selectedGroup2: validIdsInGroup2.includes(meter.meterIdAsNumber),
                    }));
                }),
            ),
        ),
    );

    private readonly tableData$: Observable<MetersForAgentTableData> = combineLatest([
        this.metersForAgentService.compareGroups$,
        this.metersForAgentService.page$.pipe(distinctUntilChanged()),
        this.metersForAgentService.pageSize$.pipe(distinctUntilChanged()),
        this.selectableMeters$,
        fromControl(this.metersForAgentService.tableFormGroup.controls.filterFormGroup),
    ]).pipe(
        map(([compareGroups, page, pageSize, meters, filterValues]) => {
            const filteredMeters = this.filterMeters(meters, filterValues);

            return {
                headers: headers.filter(header => header.key !== 'selectedGroup2' || compareGroups),
                compareGroups,
                page,
                pageSize,
                meters: {
                    all: meters,
                    filtered: filteredMeters,
                    paginated: filteredMeters.slice((page - 1) * pageSize, page * pageSize),
                    allInGroup1Checked: filteredMeters.every(meter => meter.selectedGroup1),
                    allInGroup2Checked: filteredMeters.every(meter => meter.selectedGroup2),
                },
            };
        }),
    );

    readonly view$: Observable<SelectMetersTableView> = combineLatest([
        this.formGroups$,
        this.tableData$,
    ]).pipe(
        map(([formGroups, tableData]) => ({
            ...formGroups,
            ...tableData,
        })),
    );

    constructor() {
        fromControl(this.metersForAgentService.tableFormGroup.controls.filterFormGroup)
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this.metersForAgentService.page$.next(1);
            });
        // automatically deselect meter in one group if same meter is selected in the other group
        this.formGroup$
            .pipe(
                switchMap(({controls: {idsInGroup1, idsInGroup2}}) =>
                    merge(
                        idsInGroup1.valueChanges.pipe(
                            tap(ids =>
                                idsInGroup2.setValue(
                                    idsInGroup2.value.filter(id => !ids.includes(id)),
                                    {emitEvent: false},
                                ),
                            ),
                        ),
                        idsInGroup2.valueChanges.pipe(
                            tap(ids =>
                                idsInGroup1.setValue(
                                    idsInGroup1.value.filter(id => !ids.includes(id)),
                                    {emitEvent: false},
                                ),
                            ),
                        ),
                    ),
                ),
                takeUntilDestroyed(),
            )
            .subscribe();
    }

    metersSelected(meters: MeterForAgent[], control: FormControl<number[]>, event: Event): void {
        const input = event.target as HTMLInputElement;
        const ids = meters.map(meter => meter.meterIdAsNumber);
        const selectedIds = control.getRawValue();

        const updatedIds = input.checked
            ? uniq([...selectedIds, ...ids])
            : selectedIds.filter(id => !ids.includes(id));

        control.setValue([...updatedIds]);
    }

    onSort(sorting: MetersForAgentSorting, meters: SelectableAgentMeter[]): void {
        applySortingToHeaders(this.headers(), sorting);

        const sortedMeters = this.sortMeters(meters, sorting);
        this.sortedMetersSubject$.next(sortedMeters);
    }

    onPageChange(page: number): void {
        this.metersForAgentService.page$.next(page);
    }

    onPageSizeChange(pageSize: number): void {
        this.metersForAgentService.pageSize$.next(pageSize);
    }

    private sortMeters(
        meters: SelectableAgentMeter[],
        sorting: MetersForAgentSorting,
    ): SelectableAgentMeter[] {
        const {column, direction} = sorting;

        return [...meters].sort((a, b) => {
            // ensure replaced meters appear below their replacement meters
            if (a.replacedByMeterId === b.meterId) {
                return 1;
            }
            if (b.replacedByMeterId === a.meterId) {
                return -1;
            }

            // primary sort
            switch (column) {
                case 'meterNo':
                    return numericLocaleComparator(
                        (meter: SelectableAgentMeter) => meter[column],
                        direction,
                    )(a, b);
                case 'proBraLoc':
                    return proBraLocComparator(direction)(a, b);
                case 'usageAndUnit':
                    return keyComparator((meter: SelectableAgentMeter) => meter[column])(a, b);
                default:
                    return comparator({
                        selector: (meter: SelectableAgentMeter) => meter[column],
                        order: direction,
                    })(a, b);
            }
        });
    }

    private toSearchString(value: unknown): string {
        if (value == null) {
            return '';
        }
        if (value instanceof DateTime) {
            return formatDate(value, this.translateService.language);
        }
        if (typeof value === 'boolean') {
            const selectedTranslation = value ? 'common_selected' : 'common_not_selected';
            return this.translateService.translate(selectedTranslation).toLowerCase();
        }
        return String(value).toLowerCase();
    }

    private matches(meterValue: unknown, filterValue: unknown): boolean {
        const filterString = this.toSearchString(filterValue);
        if (filterString === '') {
            return true;
        }

        const meterString = this.toSearchString(meterValue);

        // booleans must be exactly equal
        if (typeof meterValue === 'boolean') {
            return meterString === filterString;
        }

        return meterString.includes(filterString);
    }

    private filterMeters(
        meters: SelectableAgentMeter[],
        filterValues: FilterMetersFormGroup['value'],
    ): SelectableAgentMeter[] {
        return meters.filter(meter =>
            Object.entries(filterValues).every(([filterKey, filterValue]) => {
                if (filterValue == null) {
                    return true;
                }

                const filterStrings = Array.isArray(filterValue)
                    ? filterValue.filter(item => item.selected).map(item => item.name)
                    : [filterValue];

                if (filterStrings.length === 0) {
                    return true;
                }

                const meterValue = meter[filterKey as keyof SelectableAgentMeter];
                return filterStrings.some(value => this.matches(meterValue, value));
            }),
        );
    }
}
